<template>
  <section :class="['dumpster-selection']">

    <div v-if="noService">
      <h2 class="dumpster-selection__header">Recommended dumpsters for your project.</h2>
      <div class="dumpster-selection__no-service-card">
        <img :src="searchIcon" />
        <h1 class="dumpster-selection__no-service-card-header">Sorry, we weren't able to find a match.</h1>
        <p class="dumpster-selection__no-service-card-copy-one">While we accept a variety of waste types in hundreds of
          cities, we are not able to fulfill your request at this time. However, we're still here to support you! Please
          visit our quote form, and we'll do our best to find the ideal dumpster solution for your project.</p>
        <p class="mt-1 mb-1"><a :href=msraq target="_blank">Request A Quote</a></p>
      </div>
    </div>

    <!-- Lowest Price Plus -->
    <div v-else>
      <div :class="['dumpster-selection', {'dumpster-selection--disabled': !makoResponse }]">
        <h2 v-if="!savedCartUUID" class="dumpster-selection__header">Choose the best dumpster size for your project.</h2>

        <!-- Size Selection Buttons -->
        <ul v-if="makoResponse && !savedCartUUID" class="dumpster-selection__list">
          <li v-for="(details, size) in sizes" :key="size" class="dumpster-selection__item">
            <button
              :class="['dumpster-selection__btn', {
                'dumpster-selection__btn--isSelected': this.selectedDumpsterSize === size
              }]"
              :disabled="!makoResponse"
              @click="setSelectedDumpsterSize(size)"
              type="button"
            >
              {{size}}<br>Yard
              <p v-if="!isNaN(details.lowestPrice)" class="dumpster-selection__btn-price">
                ${{formatPrice(details.lowestPrice)}}
              </p>
              <img v-if="false" class="dumpster-selection__star" :src="bestSellerStar">
            </button>
          </li>
        </ul>

        <!-- Product Cards -->
        <div class="dumpster-selection__card">
          <div class="dumpster-selection__card-inner">
            <div class="dumpster-selection__info-wrapper">
              <img class="dumpster-selection__img mb-1" :src="dumpsterImage" />
              <p class="dumpster-selection__card-text mb-1">
                <strong>Holds:</strong>
                {{holds}}
              </p>
              <p class="dumpster-selection__card-text mb-1">
                <strong>Best For:</strong>
                {{bestFor}}
              </p>
              <p
                v-if="selectedDumpster && selectedDumpster.prohibitedItems.length > 0"
                class="dumpster-selection__card-text dumpster-selection--prohibited"
              >
                <img :src="prohibitedIcon" alt="Prohibited Icon" class="dumpster-selection__img--prohibited">
                <strong>Prohibited:</strong>
                {{formatItems(selectedDumpster.prohibitedItems)}}
              </p>
              <p
                v-if="selectedDumpster && selectedDumpster.allowedItems.length > 0"
                class="dumpster-selection__card-text dumpster-selection--allowed"
              >
                <img :src="dollarIcon" alt="Dollar Icon" class="dumpster-selection__img--dollar">
                <strong>Costs/Restrictions May Apply:</strong>
                {{formatItems(selectedDumpster.allowedItems)}}
              </p>
            </div>

            <div class="dumpster-selection__price-wrapper">
              <h3 class="dumpster-selection__card-header">
                <span class="dumpster-selection__header-text">
                  {{selectedDumpsterSize}} Yard Roll Off
                </span>
                <span
                  v-if="shouldShowStartingAt"
                  class="dumpster-selection__card-text--small"
                >
                  Starting at
                </span>
                <template v-if="selectedDumpster">
                  <span class="dumpster-selection__price">
                    ${{ Number(selectedDumpster.price).toFixed(2) }}
                  </span>
                </template>
                <span class="dumpster-selection__price" v-else>$--</span>
              </h3>

              <!-- Date Cards -->
              <div class="date-card">
                <p class="date-card__header">Next Available Delivery</p>
                <ul class="date-card__list">
                  <li class="date-card__item"
                    v-for="d in dumpstersInSizeGroup"
                    :key="d.makoId"
                    @click="setSelectedDumpster(d)"
                  >
                    <button
                      v-if="selectedDumpster"
                      :class="['date-card__button', {
                        'date-card__button--active': this.selectedDumpster.makoID === d.makoID
                      }]"
                      type="button"
                    >
                      <span class="date-card__date">{{formateDateCardDate(d.deliveryDate)}}</span>
                      <span class="date-card__price">${{formatPrice(d.price)}}</span>
                    </button>
                  </li>
                </ul>
              </div>
              <div class="dumpster-selection__lock-rate mt-1" @click="dumpsterCardLockRate">
                <img class="summary__lock-rate-icon" :src="saveIcon">
                <span class="summary__lock-rate-link">Lock in This Rate</span>
              </div>
              <h4 class="dumpster-selection__card-sub-header">What's Included:</h4>
              <ul v-if="selectedDumpster" class="dumpster-selection__card-list">
                <li>{{selectedDumpster.tonnage}} Ton(s) of Debris</li>
                <li>{{getNumericValue(selectedDumpster.rentalPeriod)}} Day Rental</li>
                <li>Delivery &amp; Pickup</li>
                <li>Disposal</li>
              </ul>
              <div class="call-for-availability" v-if="selectedDumpster && selectedDumpster.mustCall">
                <h4 class="dumpster-selection__card-sub-header">Call for Availability</h4>
                <ul class="dumpster-selection__card-list dumpster-selection__card-list--gray">
                  <li class="dumpster-selection__must-call-content" @click="openCallUsModal">
                    <a class="dumpster-selection__card-call-link">Why do I have to call?</a>
                    <img class="dumpster-selection__card-question-icon" :src="questionMarkIcon">
                  </li>
                  <li>Mon - Fri: 8 am - 6 pm EST</li>
                </ul>
              </div>
            </div>
          </div>
          <div class="dumpster-selection__btn-wrapper mt-2">
            <img class="dumpster-selection__best-seller-img" v-if="false" :src="bestSellerFlag" />
            <button
              v-if="isSelectedDumpsterInCart"
              @click="clearCart"
              :disabled="!makoResponse"
              class="dumpster-selection__remove-btn"
              type="button"
            >
              Remove
            </button>
            <button
              v-else-if="selectedDumpster && !selectedDumpster.mustCall"
              @click="handleAddToCart"
              :disabled="!makoResponse"
              class="dumpster-selection__rent-btn"
              type="button"
            >
              Add to Cart
            </button>
            <a v-else :href="`tel:${phoneNumber}`" class="dumpster-selection__rent-btn">{{phoneNumber}}</a>
          </div>
        </div>
      </div>
      <CallUsModal
      :isOpen="isCallUsModalOpen"
      :closeOnOverlayClick="true"
      @close-modal="closeCallUsModal"/>
       <InventoryModal
      :isOpen="isInventoryModalOpen"
      :selectedDumpster="selectedDumpster"
      @continue="handleInventoryContinue"
      @close-modal="closeInventoryModal"/>
    </div>
  </section>
</template>

<script>
import { mapState, mapMutations, mapActions } from 'vuex'
import config from '@/config'
import { getNumericValue, timestamp, isGoogleEventsReady, getClientID, formatItems, substituteSizeMap } from '@/utilities.js'
import CallUsModal from '@/components/CallUsModal.vue'
import InventoryModal from '@/components/InventoryModal.vue'
const { cdn, phoneNumberNoService, phoneNumber, links: { msraq } } = config

export default {
  components: { CallUsModal, InventoryModal },
  data () {
    return {
      msraq,
      dumpsterDetailsObject: {
        10: {
          holds: '50-70 Trash Bags, or 3 Pickup Truck Loads',
          bestFor: 'Attic Cleanouts, Light Demolition & Concrete Disposal'
        },
        11: {
          holds: '60-80 Trash Bags, or 3.5 Pickup Truck Loads',
          bestFor: 'Garage Cleanouts, Yard Projects & Heavy Debris Disposal'
        },
        12: {
          holds: '65-85 Trash Bags, or 3.5 Pickup Truck Loads',
          bestFor: 'Deck or Patio Projects, Basement Cleanouts & Asphalt Disposal'
        },
        13: {
          holds: '70-90 Trash Bags, or 4 Pickup Truck Loads',
          bestFor: 'Flooring projects, Multi-Room Cleanouts & Dirt Disposal'
        },
        14: {
          holds: '75-95 Trash Bags, or 4 Pickup Truck Loads',
          bestFor: 'Small Home Renovations, Junk Removal & Appliance Disposal'
        },
        15: {
          holds: '80-100 Trash Bags, or 4.5 Pickup Truck Loads',
          bestFor: 'Bathroom Remodels, Basement Decluttering & Festivals'
        },
        16: {
          holds: '90-110 Trash Bags, or 5 Pickup Truck Loads',
          bestFor: 'Flooring Removal, One-Room Renovations & Roofing Repairs'
        },
        18: {
          holds: '100-120 Trash Bags, or 5.5 Pickup Truck Loads',
          bestFor: 'Kitchen Remodels, Event Cleanups & Landscaping Projects'
        },
        20: {
          holds: '110-130 Trash Bags, or 6 Pickup Truck Loads',
          bestFor: 'Partial Home Cleanouts, Roofing Projects & General Construction'
        },
        22: {
          holds: '125-145 Trash Bags, or 6.5 Pickup Truck Loads',
          bestFor: 'Home Renovations, Landcaping Projects & Large-Scale Junk Removal'
        },
        25: {
          holds: '140-160 Trash Bags, or 7.5 Pickup Truck Loads',
          bestFor: 'Community Cleanups, Furniture Disposal & Construction Jobs'
        },
        28: {
          holds: '150-180 Trash Bags, or 8.5 Pickup Truck Loads',
          bestFor: 'Park or River Cleanups, Business Renovations & Home Cleanouts'
        },
        30: {
          holds: '170-190 Trash Bags, or 9 Pickup Truck Loads',
          bestFor: 'Home Additions, Office Cleanouts & Decluttering for a Move'
        },
        35: {
          holds: '205-225 Trash Bags, or 10.5 Pickup Truck Loads',
          bestFor: 'New Construction, Major Home Remodels & Bulky Furniture Disposal'
        },
        40: {
          holds: '230-250 Trash Bags, or 12 Pickup Truck Loads',
          bestFor: 'Full-Home Remodels, Estate Cleanouts & Commercial Renovations'
        },
        50: {
          holds: '295-315 Trash Bags, or 15 Pickup Truck Loads',
          bestFor: 'Large-Scale Demolition, Community Cleanups & Major Renovations'
        }
      },
      searchIcon: `${cdn}icons-mag-glass-x.svg`,
      saveIcon: `${cdn}icons-save-cart.svg`,
      phoneIcon: `${cdn}footer-phone-icon.svg`,
      bestSellerStar: `${cdn}best-seller-star.svg`,
      bestSellerFlag: `${cdn}best-seller-flag.svg`,
      prohibitedIcon: `${cdn}prohibited_icon_2.svg`,
      dollarIcon: `${cdn}dollar-icon_2.svg`,
      questionMarkIcon: `${cdn}icons-question-mark.svg`,
      phoneNumberNoService,
      phoneNumber,
      formatItems,
      isCallUsModalOpen: false,
      isInventoryModalOpen: false
    }
  },
  methods: {
    ...mapMutations('dumpster', [
      'setSelectedDumpster',
      'setSelectedDumpsterSize'
    ]),
    ...mapActions('cart', [
      'getDiscount'
    ]),
    formatPrice (price) {
      const [dollars] = price.split('.')
      return dollars
    },
    formateDateCardDate (date) {
      const suffix = '+'
      const [, month, day] = date.split('-')
      return `${month}/${day}${suffix}`
    },
    dumpsterCardLockRate () {
      this.$store.commit('lockRate/lockInRate', this.selectedDumpster)
    },
    addToCart () {
      if (isGoogleEventsReady()) {
        const time = timestamp()
        window.gtag('event', 'Ecommerce - RentThisDumpster', {
          event_category: 'Ecommerce',
          event_label: `${time}~${window.location.href}~${this.selectedDumpsterSize}`,
          send_to: process.env.VUE_APP_UA_ID
        })

        window.gtag('event', 'Ecommerce - RentThisDumpster', {
          event_category: 'Ecommerce',
          timestamp: time,
          dumpster_size: this.selectedDumpsterSize,
          bd_client_id: getClientID(),
          send_to: process.env.VUE_APP_GA4_ID
        })
      }
      // Reset delivery date incases of a previous dumpster was selected and delivery date set
      this.$store.commit('customer/deliveryDate', '')
      this.$store.commit('cart/item', this.selectedDumpster)
      if (this.$store.state.cart.discountCode) {
        this.getDiscount({ useCartItem: true })
      }
    },
    clearCart () {
      this.$store.commit('cart/item', null)
      this.$store.commit('customer/hasAcceptedTonnageOverages', false)
      this.$store.commit('customer/hasAcceptedRentalOverages', false)
    },
    getNumericValue,
    openCallUsModal () {
      this.isCallUsModalOpen = true
    },
    closeCallUsModal () {
      this.isCallUsModalOpen = false
    },
    openInventoryModal () {
      this.isInventoryModalOpen = true
    },
    closeInventoryModal () {
      this.isInventoryModalOpen = false
    },
    handleAddToCart () {
      if (this.selectedDumpster?.sellAsSize != null) {
        this.openInventoryModal()
      } else {
        this.addToCart()
      }
    },
    handleInventoryContinue (ackValue) {
      this.isInventoryModalOpen = false
      if (ackValue) {
        this.addToCart()
        this.$router.push('/cart/service')
      }
    }
  },
  computed: {
    ...mapState('cart', [
      'item',
      'savedCartUUID'
    ]),
    ...mapState('dumpster', [
      'dumpstersInSizeGroup',
      'makoResponse',
      'noService',
      'selectedDumpster',
      'sizes'
    ]),
    dumpsterImage () {
      const dumpsterSize = this.selectedDumpsterSize
      const imageSize = Object.entries(substituteSizeMap)
        .find(([substitute, sizes]) => sizes.includes(dumpsterSize))?.[0] ?? dumpsterSize
      return `${cdn}${imageSize}-yard-dumpster-card.png`
    },
    isSelectedDumpsterInCart () {
      return this.selectedDumpster && this.item
        ? this.selectedDumpster.netsuiteID === this.item.netsuiteID
        : false
    },
    isBestSeller () {
      return this.$store.state.dumpster.selectedDumpster.isBestSeller ?? false
    },
    selectedDumpsterSize () {
      return this.$store.state.dumpster.selectedDumpsterSize ?? 10
    },
    shouldShowStartingAt () {
      return this.selectedDumpster &&
        this.selectedDumpster.isLowestPrice &&
        this.dumpstersInSizeGroup.length > 1
    },
    holds () {
      return this.dumpsterDetailsObject[this.selectedDumpsterSize].holds
    },
    bestFor () {
      return this.dumpsterDetailsObject[this.selectedDumpsterSize].bestFor
    }
  }
}
</script>
